import { useState } from "react";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";
import { LQIntention } from "@/hooks/useLaylatulQadr";

interface NightIntentionProps {
  nightNumber: number;
  intention?: LQIntention;
  onSave: (nightNumber: number, text: string) => Promise<void>;
}

export const NightIntention = ({ nightNumber, intention, onSave }: NightIntentionProps) => {
  const [text, setText] = useState(intention?.intention || "");
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(!intention?.intention);

  const handleSave = async () => {
    if (!text.trim()) return;
    setIsSaving(true);
    await onSave(nightNumber, text.trim());
    setIsSaving(false);
    setIsEditing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.1 }}
      className="space-y-4"
    >
      <h2 className="text-xs uppercase tracking-[0.2em] text-gold flex items-center gap-3">
        <Heart className="w-4 h-4" />
        Tonight's Intention
      </h2>

      <div className="bg-[#0f0f23]/50 rounded-sm p-6 border border-gold/10 space-y-4">
        <p className="text-cream/60 text-sm italic">
          "What intention are you bringing into tonight?"
        </p>

        {isEditing ? (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What do you want to ask Allah for tonight? What weighs on your heart?"
              className="w-full bg-transparent border border-gold/20 rounded-sm p-4 text-cream/90 text-sm placeholder:text-cream/30 focus:outline-none focus:border-gold/40 resize-none min-h-[120px]"
              rows={4}
            />
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={isSaving || !text.trim()}
                className="px-5 py-2 border border-gold/50 text-gold text-xs uppercase tracking-wider hover:bg-gold/10 transition-colors rounded-sm disabled:opacity-40"
              >
                {isSaving ? "Saving..." : "Save Intention"}
              </button>
              {intention?.intention && (
                <button
                  onClick={() => { setIsEditing(false); setText(intention.intention); }}
                  className="px-5 py-2 text-cream/40 text-xs uppercase tracking-wider hover:text-cream/60 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-cream/85 leading-relaxed">{intention?.intention}</p>
            <button
              onClick={() => setIsEditing(true)}
              className="text-gold/60 text-xs uppercase tracking-wider hover:text-gold transition-colors"
            >
              Edit intention
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};
